import { useCallback } from 'react';

import {
	useContentTreeSelectionActions,
	useSelectionCount,
} from '@confluence/content-tree-actions';
import type { ItemId } from '@confluence/tree';

import { usePageTreeActions } from '../usePageTreeState';
import type { ContentTreeItem } from '../data-transformers';

type ItemWithParent = { id: ItemId; parentId?: ItemId };

export const useNestedSelect = (pageTreeItem: ContentTreeItem) => {
	const { selectNestedContent, updateCount } = useContentTreeSelectionActions();

	const { selectDescendantCount } = useSelectionCount();
	const { getState } = usePageTreeActions();

	const selectContentAndDescendants = useCallback(() => {
		const { pages } = getState();
		const descendants: ItemWithParent[] = [];
		const parents = new Set<ItemId>();
		const traverse = (currItem: ContentTreeItem) => {
			descendants.push({ id: currItem.id, parentId: currItem.parentId ?? undefined });
			if (currItem.hasChildren) {
				parents.add(currItem.id);
			}
			const children = currItem.children || [];
			for (const childId of children) {
				const child = pages[childId];
				if (child && child.data.status != 'DRAFT') {
					traverse(child);
				}
			}
		};
		traverse(pageTreeItem);

		selectNestedContent(descendants);

		if (parents.size > 0) {
			selectDescendantCount(parents);
		} else {
			descendants.forEach(({ id }) => {
				updateCount(id, 0);
			});
		}
	}, [getState, pageTreeItem, selectNestedContent, selectDescendantCount, updateCount]);

	return selectContentAndDescendants;
};
